import { Injectable, inject, signal } from '@angular/core';
import { LookupApiService } from './lookup-api.service';
import { LookupCode } from './lookup-codes';
import { LookupResponse, PortalResponse, UserRoleResponse } from '../models/common.model';

@Injectable({ providedIn: 'root' })
export class LookupStore {
  private readonly lookupApiService = inject(LookupApiService);

  readonly lookups = signal<Record<string, LookupResponse[]>>({});
  readonly portals = signal<PortalResponse[]>([]);
  readonly userRoles = signal<UserRoleResponse[]>([]);
  readonly loading = signal(false);

  // The items of a lookup, in the order the server returned them. Empty until it has been loaded.
  items(code: LookupCode): LookupResponse[] {
    return this.lookups()[code] ?? [];
  }

  // Loads the given lookups in a single request and merges them into whatever is already loaded.
  load(codes: readonly LookupCode[]): void {
    this.loading.set(true);

    this.lookupApiService.getLookups(codes).subscribe({
      next: (loaded) => {
        this.lookups.update((current) => ({ ...current, ...loaded }));
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  loadUserManagementLookups(): void {
    this.loading.set(true);
    let pendingRequests = 2;

    const complete = (): void => {
      pendingRequests -= 1;
      if (pendingRequests === 0) {
        this.loading.set(false);
      }
    };

    this.lookupApiService.getPortals().subscribe({
      next: (items) => this.portals.set(items),
      error: () => complete(),
      complete,
    });

    this.lookupApiService.getUserRoles().subscribe({
      next: (items) => this.userRoles.set(items),
      error: () => complete(),
      complete,
    });
  }
}
